import React from 'react'
import { useTranslation } from 'react-i18next'
import ChevronRightSvg from '../other/svg/ChevronRightSvg'
import MoreHorizontalSvg from '../other/svg/MoreHorizontalSvg'
import PencilSvg from '../other/svg/PencilSvg'
import { MotorbikeStatus } from 'src/redux/motorbikesReducer'

interface SpecItem {
  label: string
  value: string
  wide?: boolean
}

interface VehicleDetailsCardProps {
  title: string
  status: MotorbikeStatus | null
  specs: SpecItem[]
  detailSpecs?: SpecItem[]
  onBack: () => void
  onEdit: () => void
  backLabel?: string
  specColumns?: number
  largeTitle?: boolean
  editVariant?: 'pencil' | 'more'
}

interface SpecCellProps {
  spec: SpecItem
  inGrid: boolean
}

const SpecCell = ({ spec, inGrid }: SpecCellProps): JSX.Element => {
  return (
    <div
      className={`inline-flex flex-col justify-start items-start min-w-0 ${
        spec.wide === true || inGrid ? '' : 'min-w-32'
      }`}>
      <div className="self-stretch justify-center text-foregrounds-fg-muted text-xs font-medium font-Montserrat leading-4">
        {spec.label}
      </div>
      <div className="self-stretch justify-start text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5 break-words">
        {spec.value}
      </div>
    </div>
  )
}

const statusBadgeShadow = '0px 0px 0px 1px rgba(0,0,0,0.08)'

const editButtonShadow = '0px 0px 0px 1px rgba(0,0,0,0.08), 0px 1px 2px 0px rgba(0,0,0,0.12)'

const VehicleDetailsCard = ({
  title,
  status,
  specs,
  detailSpecs = [],
  onBack,
  onEdit,
  backLabel,
  specColumns,
  largeTitle = false,
  editVariant = 'pencil'
}: VehicleDetailsCardProps): JSX.Element => {
  const { t } = useTranslation()
  const statusLabel = status === 'sold' ? t('my_vehicles.sold') : t('my_vehicles.in_stock')
  const breadcrumbLabel = backLabel ?? t('my_vehicles.title')

  return (
    <div className="self-stretch flex flex-col justify-start items-start">
      <div className="self-stretch h-14 bg-Estigroup-primary rounded-tl-lg rounded-tr-lg border-l border-r border-t border-neutral-200 flex items-center">
        <div className="self-stretch px-4 flex flex-1 justify-start items-center gap-2 overflow-hidden">
          <div className="flex-1 flex justify-start items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="justify-start text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {breadcrumbLabel}
            </button>
            <ChevronRightSvg className="size-3.5 text-foregrounds-fg-on-color" />
            <div className="justify-start text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {title}
            </div>
          </div>
        </div>
      </div>
      <div className="self-stretch px-4 pt-3 pb-4 bg-backgrounds-bg-field-component rounded-bl-lg rounded-br-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base inline-flex justify-start items-start gap-3">
        <div className="flex-1 inline-flex flex-col justify-start items-start gap-3">
          <div className="self-stretch inline-flex justify-start items-center gap-3">
            <div
              className={`justify-start text-foregrounds-fg-base font-semibold font-Montserrat leading-8 ${
                largeTitle ? 'text-[20px]' : 'text-xl'
              }`}>
              {title}
            </div>
            {status !== null && (
              <div
                className="px-2 py-0.5 bg-backgrounds-bg-component rounded-md flex justify-center items-center overflow-hidden"
                style={{ boxShadow: statusBadgeShadow }}>
                <div className="text-center justify-start text-foregrounds-fg-muted text-xs font-semibold font-Montserrat leading-5">
                  {statusLabel}
                </div>
              </div>
            )}
          </div>
          <div
            className={
              typeof specColumns === 'number'
                ? 'self-stretch grid gap-x-6 gap-y-3'
                : 'self-stretch inline-flex justify-start items-center gap-6 flex-wrap content-center overflow-hidden'
            }
            {...(typeof specColumns === 'number'
              ? { style: { gridTemplateColumns: `repeat(${specColumns}, minmax(0, 1fr))` } }
              : {})}>
            {specs.map((spec) => (
              <SpecCell key={spec.label} spec={spec} inGrid={typeof specColumns === 'number'} />
            ))}
          </div>
          {detailSpecs.length > 0 && (
            <div className="self-stretch grid grid-cols-2 gap-x-6 gap-y-3 border-t border-borders-border-base pt-3 md:grid-cols-5">
              {detailSpecs.map((spec) => (
                <SpecCell key={spec.label} spec={spec} inGrid />
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('my_vehicles.edit')}
          className="p-2.5 bg-backgrounds-bg-component rounded-md flex justify-center items-center overflow-hidden"
          style={{ boxShadow: editButtonShadow }}>
          {editVariant === 'more' ? (
            <MoreHorizontalSvg className="size-3.5 text-icon-secondary" />
          ) : (
            <PencilSvg className="size-3.5 text-icon-secondary" />
          )}
        </button>
      </div>
    </div>
  )
}

export default VehicleDetailsCard
